import { useEffect } from 'react';
import type React from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { ImageUploadProvider } from 'src/app/shared/hooks/imageUpload';
import { useToast } from 'src/app/shared/toast';

import { useAddEndorsement } from '../../hooks/useAddEndorsement';
import type { EndorsementFormSchema } from '../../shared/validation';
import { EndorsementForm } from '../EndorsementForm';

import { contentContainer, formSection, pageContainer, previewSection } from './AddEndorsement.styles';

const AddEndorsementContent: React.FC<{ petitionSlug: string }> = ({ petitionSlug }) => {
	const { translate } = useI18n();
	const track = useTracking();
	const { data, actions, uploader } = useAddEndorsement({ petitionSlug });
	const { showToast } = useToast();

	// Track page view on mount
	useEffect(() => {
		void track('petitions_dashboard_view', { context: 'endorsements' });
	}, [track]);

	// Show error toast when error occurs
	useEffect(() => {
		if (data.error) {
			showToast({
				variant: 'error',
				duration: 7000,
				message:
					data.error.type === 'VALIDATION'
						? translate('corgi.petition.starter_dashboard_v2.endorsements.add.form_validation_error')
						: data.error.message,
			});
			actions.clearError();
		}
	}, [data.error, showToast, actions, translate]);

	const handleSubmit = (formData: EndorsementFormSchema) => {
		// Update form with submitted data
		if (formData.name) actions.handleFieldChange('name', formData.name);
		if (formData.title) actions.handleFieldChange('title', formData.title);
		if (formData.statement) actions.handleFieldChange('statement', formData.statement);
		if (formData.url) actions.handleFieldChange('url', formData.url);

		// Trigger next step
		actions.handleNext();
	};

	const handleInvalidSubmit = () => {
		// Show toast when form validation fails
		showToast({
			variant: 'error',
			duration: 7000,
			message: translate('corgi.petition.starter_dashboard_v2.endorsements.add.form_validation_error'),
		});
	};

	const handleConfirmSubmit = async () => {
		// Check if terms are agreed before submitting
		if (!data.isTermsAgreed) {
			showToast({
				variant: 'error',
				duration: 7000,
				message: translate('corgi.petition.starter_dashboard_v2.endorsements.add.terms_required_error'),
			});
			return;
		}
		await actions.confirmSubmit();
	};

	return (
		<EndorsementForm
			data-qa="add-endorsement-form"
			title={translate('corgi.petition.starter_dashboard_v2.endorsements.add.page_title')}
			backButtonAriaLabel={translate('corgi.petition.starter_dashboard_v2.endorsements.add.back_button_aria_label')}
			defaultValues={data.formData}
			isSubmitting={data.isSubmitting}
			isHelpModalOpen={data.isHelpModalOpen}
			isSubmitModalOpen={data.isSubmitModalOpen}
			onBack={actions.handleBack}
			onSubmit={handleSubmit}
			onInvalidSubmit={handleInvalidSubmit}
			onHelpClick={actions.handleHelpClick}
			onCloseHelpModal={actions.closeHelpModal}
			onCloseSubmitModal={actions.closeSubmitModal}
			onConfirmSubmit={handleConfirmSubmit}
			pageContainer={pageContainer()}
			contentContainer={contentContainer()}
			formSection={formSection()}
			previewSection={previewSection()}
			testId="add-endorsement-page"
			uppy={uploader.uppy}
			image={data.image}
			isTermsAgreed={data.isTermsAgreed}
			onTermsChange={actions.handleTermsChange}
		/>
	);
};

export const AddEndorsement: React.FC = () => {
	const { slug } = useKnownAvailableParams<{ slug: string }>();

	return (
		<ImageUploadProvider>
			<AddEndorsementContent petitionSlug={slug} />
		</ImageUploadProvider>
	);
};
